// Zweck: Allgemeine Lagerfunktion und geführter Test auf dem Handy.
// Kontext: Eigene Buchungswarteschlange, bestehende Anmeldung und API. Stand: 20.10.2026.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import WarehouseProjectPicker from '../components/WarehouseProjectPicker';
import WarehouseQuantity from '../components/WarehouseQuantity';
import WarehouseQuantityReview from '../components/WarehouseQuantityReview';
import WarehouseHeroStatus from '../components/WarehouseHeroStatus';
import WarehouseArticleDetails from '../components/WarehouseArticleDetails';
import {
  WarehouseHomeActions,
  WarehouseIcon,
  WarehouseSteps,
} from '../components/WarehousePresentation';
import { useAuth } from '../contexts/AuthContext';
import {
  getCachedProfile,
  getIdToken,
  wasAuthenticated,
} from '../services/googleAuthSession';
import { warehouseApi } from '../services/api/warehouse';
import { warehouseCodeMatches } from '../services/warehouseCodes';
import {
  emptyWarehouse,
  syncWarehouse,
  warehouseKey,
  warehouseStock,
  warehouseStore,
  warehouseAccessStatus,
  withWarehouseSnapshot,
  confirmedWarehouseRequests,
} from '../services/warehouseQueue';
import {
  demoTransport,
  resetDemo,
  demoConflict,
} from '../services/warehouseDemo';
import type {
  WarehouseBooking,
  WarehouseDraft,
  WarehouseLocal,
  WarehouseQueued,
  WarehouseLine,
} from '../interfaces/warehouse';
import { readWarehouseCode } from '../services/readWarehouseCode';
import './warehouse.css';

const labels = {
  receipt: 'Wareneingang',
  issue: 'Material ausgeben',
  return: 'Material zurücknehmen',
  correction: 'Buchung korrigieren',
  opening: 'Anfangsbestand',
};
const status = {
  pending: 'Auf diesem Gerät gespeichert',
  unknown: 'Bestätigung fehlt',
  confirmed: 'In 360 gebucht',
  rejected: 'Nicht gebucht',
  cancelled: 'Verworfen · nicht gebucht',
  superseded: 'Durch Berichtigung ersetzt',
};
const number = (n: number) =>
  n.toLocaleString('de-DE', { maximumFractionDigits: 3 });
function focusField(id: string, select = false) {
  requestAnimationFrame(() => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    input?.focus();
    if (select) input?.select();
    input?.scrollIntoView({ block: 'center' });
  });
}
const tests = [
  [
    'receipt',
    'Wareneingang',
    '5 Kupplungen (12345) ins Zentrallager einbuchen.',
    17,
  ],
  [
    'issue',
    'Ausgabe',
    '3 Kupplungen (12345) für Müller aus dem Zentrallager ausgeben.',
    9,
  ],
  [
    'return',
    'Rückgabe',
    '2 verwendbare Kupplungen (12345) von Müller ins Zentrallager zurücknehmen.',
    14,
  ],
  [
    'prepared',
    'Packliste',
    'Müller wählen → Materialliste verwenden. 8 Kupplungen und 20 Schellen abhaken. Zusätzlich 2 Dichtungen hinzufügen. Rohr offen lassen. Dann bestätigen.',
    4,
  ],
  [
    'interruption',
    'Unterbrechung',
    'Eine Ausgabe mit 3 Kupplungen beginnen. Bestand nachschlagen, App neu laden und die Liste fortsetzen. Nicht buchen.',
    12,
  ],
  [
    'offline',
    'Offline',
    '3 Kupplungen für Müller offline bestätigen. App neu laden, dann Testverbindung einschalten.',
    9,
  ],
  [
    'unknown',
    'Antwortverlust',
    '3 Kupplungen für Müller ausgeben. Danach „Übertragung prüfen“ wählen.',
    9,
  ],
  [
    'conflict',
    'Bestandskonflikt',
    '3 Kupplungen offline ausgeben. „Andere Lagerkraft bucht 11“ wählen, dann verbinden. Abgelehnten Vorgang auf tatsächlich 1 Kupplung berichtigen.',
    0,
  ],
] as const;
const testEnabled =
  import.meta.env.DEV || import.meta.env.MODE === 'warehouse-test';

export default function WarehouseScreen({ demo = false }: { demo?: boolean }) {
  const { user, isLoading } = useAuth();
  const [connected, setConnected] = useState(navigator.onLine);
  const cached = !connected && wasAuthenticated() ? getCachedProfile() : null;
  const owner = demo ? 'demo-user' : (user?.email ?? cached?.email ?? '');
  const key = warehouseKey(owner, demo ? 'demo' : 'live');
  const store = useMemo(() => warehouseStore(), []);
  const [data, setData] = useState<WarehouseLocal>(emptyWarehouse);
  const dataRef = useRef(data);
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const [ready, setReady] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [loadedKey, setLoadedKey] = useState('');
  const [windowAccess, setWindowAccess] = useState<{
    key: string;
    state: 'waiting' | 'owned' | 'other' | 'unsupported';
  }>({ key: '', state: 'waiting' });
  const [windowAttempt, setWindowAttempt] = useState(0);
  const ownsWindow = useRef(false);
  const windowReady =
    windowAccess.key === key && windowAccess.state === 'owned';
  const [view, setView] = useState<
    'home' | 'draft' | 'stock' | 'history' | 'review'
  >('home');
  const [query, setQuery] = useState(''),
    [locationId, setLocation] = useState('central');
  const [visibleLimit, setVisibleLimit] = useState(20);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const historyLoading = useRef(false);
  async function loadOlderBookings() {
    const cursor = dataRef.current.snapshot?.nextBookingId;
    if (
      !cursor ||
      !transport.history ||
      historyLoading.current ||
      sending.current ||
      busy ||
      !ownsWindow.current ||
      !onlineRef.current
    )
      return;
    historyLoading.current = true;
    setLoadingHistory(true);
    setBusy(true);
    const currentGeneration = generation.current;
    try {
      const page = await transport.history(cursor);
      await writes.current;
      if (generation.current !== currentGeneration || !ownsWindow.current)
        return;
      const current = dataRef.current;
      if (current.snapshot?.nextBookingId !== cursor) return;
      const ids = new Set(current.snapshot.bookings.map((b) => b.requestId));
      await save({
        ...current,
        snapshot: {
          ...current.snapshot,
          bookings: [
            ...current.snapshot.bookings,
            ...page.bookings.filter((b) => {
              if (ids.has(b.requestId)) return false;
              ids.add(b.requestId);
              return true;
            }),
          ],
          nextBookingId: page.nextBookingId,
        },
      });
      setVisibleLimit((n) => n + 20);
    } catch (e) {
      if (generation.current !== currentGeneration) return;
      const denied = warehouseAccessStatus(e);
      if (denied) {
        const blocked = { ...dataRef.current, accessDenied: denied };
        dataRef.current = blocked;
        setData(blocked);
        try {
          await save(blocked);
        } catch {
          setError(
            'Zugriff gesperrt. Die Sperre konnte auf diesem Gerät nicht gespeichert werden.',
          );
        }
      } else
        setError(
          'Ältere Buchungen nicht geladen. Verbindung prüfen und erneut versuchen.',
        );
    } finally {
      historyLoading.current = false;
      setLoadingHistory(false);
      if (generation.current === currentGeneration) setBusy(false);
    }
  }
  useEffect(() => setVisibleLimit(20), [query, view]);
  const [test, setTest] = useState('receipt'),
    [offline, setOffline] = useState(false),
    [assessment, setAssessment] = useState('');
  const [verification, setVerification] = useState('');
  const [notice, setNotice] = useState('');
  const [discardRequest, setDiscardRequest] = useState<string | null>(null);
  useEffect(() => setDiscardRequest(null), [view, data.draft?.requestId]);
  const [feedback, setFeedback] = useState('');
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [draftStep, setDraftStep] = useState<'context' | 'items'>('context');
  const [scanning, setScanning] = useState(false);
  const scanSequence = useRef(0);
  const scanContext = JSON.stringify([
    key,
    view,
    draftStep,
    data.draft?.requestId,
    data.draft?.locationId,
    data.draft?.projectId,
  ]);
  const scanContextRef = useRef(scanContext);
  scanContextRef.current = scanContext;
  useEffect(() => {
    const pendingScans = scanSequence;
    ++pendingScans.current;
    setScanning(false);
    return () => {
      ++pendingScans.current;
    };
  }, [scanContext]);
  useEffect(() => setDraftStep('context'), [data.draft?.requestId]);
  const lost = useRef(false),
    sending = useRef(false),
    generation = useRef(0);
  const writes = useRef<Promise<unknown>>(Promise.resolve()),
    revision = useRef(0);
  useEffect(() => {
    if (!owner) return;
    let disposed = false;
    let acquired = false;
    let release = () => {};
    const held = new Promise<void>((resolve) => {
      release = resolve;
    });
    const locks = navigator.locks;
    setWindowAccess({ key, state: 'waiting' });
    if (!locks) {
      setWindowAccess({ key, state: 'unsupported' });
      return;
    }
    // Ein gemeinsamer Entwurf darf nicht von zwei Fenstern überschrieben werden.
    // Die zweite Ansicht bleibt gesperrt, statt Daten zusammenzuraten.
    void locks
      .request(key + ':window', { ifAvailable: true }, async (lock) => {
        if (disposed) return;
        if (!lock) {
          setWindowAccess({ key, state: 'other' });
          return;
        }
        acquired = true;
        ownsWindow.current = true;
        setWindowAccess({ key, state: 'owned' });
        await held;
      })
      .catch(() => {
        if (!disposed) setWindowAccess({ key, state: 'unsupported' });
      });
    return () => {
      disposed = true;
      ownsWindow.current = false;
      if (!acquired) {
        release();
        return;
      }
      // Laufenden Versand und lokale Schreibvorgänge abschließen, bevor ein
      // anderes Fenster den Zustand lädt. Keine Daten beim Tabwechsel verlieren.
      void locks
        .request(key, async () => {
          await writes.current.catch(() => undefined);
        })
        .catch(() => undefined)
        .finally(release);
    };
  }, [key, owner, windowAttempt]);
  const onlineRef = useRef(!offline);
  onlineRef.current = !offline && connected;
  useEffect(() => {
    const connectionChanged = () => setConnected(navigator.onLine);
    window.addEventListener('online', connectionChanged);
    window.addEventListener('offline', connectionChanged);
    return () => {
      window.removeEventListener('online', connectionChanged);
      window.removeEventListener('offline', connectionChanged);
    };
  }, []);
  const transport = useMemo(
    () =>
      demo
        ? demoTransport(() => {
            const v = lost.current;
            lost.current = false;
            return v;
          })
        : warehouseApi,
    [demo],
  );
  const snapshot = data.snapshot,
    draft = data.draft;
  const article = (id: string) => snapshot?.articles.find((x) => x.id === id);
  function effect(d: WarehouseBooking, line: WarehouseLine) {
    const original =
      snapshot?.bookings.find((b) => b.requestId === d.correctsId)?.booking ??
      data.queue.find(
        (entry) =>
          entry.state === 'confirmed' &&
          entry.booking.requestId === d.correctsId,
      )?.booking;
    const sign =
      d.kind === 'issue'
        ? -1
        : d.kind === 'correction'
          ? (original?.kind === 'issue' ? -1 : 1) *
            (line.addInsteadOfReverse ? 1 : -1)
          : 1;
    return sign * line.quantity;
  }
  const save = useCallback(
    async (next: WarehouseLocal) => {
      const serial = ++revision.current;
      const persisted = structuredClone(next);
      const task = writes.current
        .catch(() => undefined)
        .then(() => store.setItem(key, persisted));
      writes.current = task;
      await task;
      if (ownerRef.current === owner && serial === revision.current) {
        dataRef.current = persisted;
        setData(persisted);
      }
    },
    [key, owner, store],
  );
  const sync = useCallback(async () => {
    if (
      historyLoading.current ||
      sending.current ||
      !owner ||
      !onlineRef.current ||
      !ownsWindow.current
    )
      return;
    sending.current = true;
    setBusy(true);
    setError('');
    const currentGeneration = generation.current;
    const maySend = () =>
      ownerRef.current === owner &&
      generation.current === currentGeneration &&
      onlineRef.current &&
      ownsWindow.current &&
      (demo || !!getIdToken());
    try {
      const run = async () => {
        // Vor offenen Buchungen die aktuelle Freigabe prüfen. Nur Netzwerkfehler
        // erlauben weiterhin lokale Eingaben; bekannte Sperren überleben Neustarts.
        if (!maySend()) return;
        const confirmedBeforeRead = confirmedWarehouseRequests(dataRef.current);
        const fresh = await transport.snapshot();
        if (!maySend()) return;
        await save({
          ...withWarehouseSnapshot(dataRef.current, fresh, confirmedBeforeRead),
          accessDenied: undefined,
        });
        await syncWarehouse(dataRef.current, transport, save, maySend);
      };
      if (navigator.locks)
        await navigator.locks.request(key, async () => {
          // Eine gerade geänderte Menge zuerst sichern, bevor der Abgleich
          // den gespeicherten Stand lädt und erneut schreibt.
          await writes.current;
          const persisted = await store.getItem<WarehouseLocal>(key);
          if (persisted) dataRef.current = persisted;
          await run();
        });
      else await run();
    } catch (e) {
      if (
        ownerRef.current !== owner ||
        generation.current !== currentGeneration
      )
        return;
      const denied = warehouseAccessStatus(e);
      if (denied) {
        const blocked = { ...dataRef.current, accessDenied: denied };
        // Auch bei vollem Gerätespeicher sofort sperren; vorhandene Buchungen bewahren.
        dataRef.current = blocked;
        setData(blocked);
        try {
          await save(blocked);
        } catch {
          setError(
            'Zugriff gesperrt. Die Sperre konnte auf diesem Gerät nicht gespeichert werden.',
          );
        }
        return;
      }
      setError(
        e instanceof Error
          ? e.message
          : 'Nicht gespeichert. Ansicht offen lassen.',
      );
    } finally {
      sending.current = false;
      if (ownerRef.current === owner) setBusy(false);
    }
  }, [demo, key, owner, save, store, transport]);
  useEffect(() => {
    let disposed = false;
    const initializedGeneration = ++generation.current;
    setReady(false);
    setLoadedKey('');
    setData(emptyWarehouse());
    dataRef.current = emptyWarehouse();
    if (!windowReady) return;
    if (!owner) {
      setReady(true);
      return;
    }
    (async () => {
      try {
        let next =
          (await store.getItem<WarehouseLocal>(key)) ?? emptyWarehouse();
        if (demo) {
          const saved = await store.getItem<{ id: string; offline: boolean }>(
            key + ':test',
          );
          if (saved) {
            setTest(saved.id);
            setOffline(saved.offline);
            onlineRef.current = !saved.offline;
          }
        }
        if (navigator.onLine && !offline) {
          try {
            next = {
              ...withWarehouseSnapshot(
                next,
                await transport.snapshot(),
                confirmedWarehouseRequests(next),
              ),
              accessDenied: undefined,
            };
          } catch (e) {
            const denied = warehouseAccessStatus(e);
            if (denied) next = { ...next, accessDenied: denied };
            else if (!next.snapshot) throw e;
          }
        }
        if (!disposed) {
          await save(next);
          setLoadedKey(key);
          if (
            next.snapshot &&
            !next.snapshot.locations.some((x) => x.id === locationId)
          )
            setLocation(next.snapshot.locations[0]?.id ?? '');
          setReady(true);
        }
      } catch (e) {
        if (!disposed) {
          setError(
            e instanceof Error
              ? e.message
              : 'Lagerdaten fehlen. Einmal online öffnen.',
          );
          setReady(true);
          setLoadedKey(key);
        }
      }
    })();
    return () => {
      disposed = true;
      generation.current = initializedGeneration + 1;
    };
    // Wechsel der Identität startet einen neuen, getrennten Datenbereich.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, windowReady]);
  useEffect(() => {
    if (ready && windowReady && !offline && connected) void sync();
  }, [offline, connected, ready, sync, windowReady]);
  async function change(fn: (s: WarehouseLocal) => void) {
    if (
      busy ||
      sending.current ||
      dataRef.current.accessDenied ||
      !ownsWindow.current
    )
      return;
    setError('');
    setBusy(true);
    try {
      const next = structuredClone(dataRef.current);
      fn(next);
      await save(next);
      return true;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Nicht gespeichert. Keine Buchung bestätigt.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function editDraft(patch: Partial<WarehouseDraft>) {
    if (sending.current || dataRef.current.accessDenied || !ownsWindow.current)
      return;
    const next = structuredClone(dataRef.current);
    if (!next.draft) return;
    Object.assign(next.draft, patch);
    dataRef.current = next;
    setData(next);
    try {
      await save(next);
      setError('');
      return true;
    } catch {
      setError(
        'Änderung nicht gespeichert. Ansicht offen lassen und erneut versuchen.',
      );
      return false;
    }
  }
  async function start(kind: WarehouseBooking['kind']) {
    if (draft) {
      setError(
        kind === draft.kind
          ? ''
          : `„${labels[draft.kind]}“ ist noch offen. Diese Liste zuerst abschließen oder verwerfen, bevor du „${labels[kind]}“ beginnst.`,
      );
      setView('draft');
      return;
    }
    if (
      await change((s) => {
        s.draft = {
          requestId: crypto.randomUUID(),
          kind,
          locationId,
          projectId: null,
          reason: null,
          lines: [],
          checked: [],
        };
      })
    ) {
      setView('draft');
      setQuery('');
      setNotice('');
      setTestPanelOpen(false);
    }
  }
  function validateContext(d: WarehouseDraft) {
    if ((d.reason?.length ?? 0) > 500)
      return 'Bitte die Begründung auf höchstens 500 Zeichen kürzen.';
    if (
      !d.locationId ||
      !snapshot?.locations.some((x) => x.id === d.locationId)
    )
      return 'Bitte einen Lagerort wählen.';
    if (
      (d.kind === 'issue' || d.kind === 'return') &&
      !d.projectId &&
      !d.reason?.trim()
    )
      return 'Bitte Auftrag wählen oder Ausnahme begründen.';
    if (d.kind === 'correction' && !d.reason?.trim())
      return 'Bitte den Grund der Korrektur eintragen.';
    return '';
  }
  function continueToItems() {
    const current = dataRef.current.draft;
    if (!current) return;
    const message = validateContext(current);
    setError(message);
    if (message) focusField('wh-error');
    else {
      setDraftStep('items');
      setNotice('');
      focusField('wh-items-title');
    }
  }
  function validate(d: WarehouseDraft) {
    const contextError = validateContext(d);
    if (contextError) return contextError;
    if (!d.lines.length) return 'Bitte mindestens einen Artikel hinzufügen.';
    if (d.lines.length > 100)
      return 'Eine Buchung kann höchstens 100 Artikel enthalten. Bitte die Liste aufteilen.';
    for (const l of d.lines) {
      const a = article(l.articleId);
      if (a?.tracked)
        return 'Seriennummernpflichtiger Artikel: aus dieser Liste entfernen und unter Geräte und Seriennummern buchen.';
      if (
        !a ||
        !Number.isFinite(l.quantity) ||
        l.quantity <= 0 ||
        l.quantity > 999999999 ||
        Math.abs(Math.round(l.quantity * 1000) - l.quantity * 1000) >
          0.0000001 ||
        (!a.fractional && !Number.isInteger(l.quantity))
      )
        return 'Bitte positive Mengen in der angegebenen Einheit eintragen.';
      if (
        warehouseStock(data, l.articleId, d.locationId) + effect(d, l) < 0 &&
        !l.negativeReason?.trim()
      )
        return `Minusbestand für ${a.name}: Menge prüfen oder Abweichung begründen.`;
    }
    return '';
  }
  async function confirm() {
    const current = dataRef.current.draft;
    if (!current) return;
    const message = validate(current);
    if (message) {
      setError(message);
      focusField('wh-error');
      return;
    }
    const booking: WarehouseBooking = {
      requestId: current.requestId,
      kind: current.kind,
      locationId: current.locationId,
      projectId: current.projectId,
      reason: current.reason,
      lines: current.lines,
      listId: current.listId,
      listVersion: current.listVersion,
      replacesId: current.replacesId,
      correctsId: current.correctsId,
    };
    if (
      await change((s) => {
        if (s.queue.some((q) => q.booking.requestId === booking.requestId))
          return;
        if (booking.replacesId) {
          const old = s.queue.find(
            (q) => q.booking.requestId === booking.replacesId,
          );
          if (old) old.state = 'superseded';
        }
        s.queue.push({ booking, state: 'pending' });
        s.draft = null;
      })
    ) {
      setQuery('');
      setNotice(
        offline || !connected
          ? 'Liste auf diesem Gerät gespeichert. Öffne die App mit Internet und deinem Konto, damit sie übertragen wird.'
          : 'Liste gespeichert. Den Buchungsstatus siehst du unten.',
      );
      setView('history');
      await sync();
    }
  }
  async function add(id: string, keepScannerFocus = false) {
    const current = dataRef.current.draft;
    if (!current || busy || sending.current) return;
    if (article(id)?.tracked) {
      setError('Für diesen Artikel die Geräte und Seriennummern öffnen.');
      return;
    }
    const context = scanContextRef.current;
    if (current.lines.some((l) => l.articleId === id)) {
      setQuery('');
      setNotice(
        `${article(id)?.name} steht bereits auf der Liste. Die Menge wurde nicht erhöht; bei Bedarf die Gesamtmenge ändern.`,
      );
      focusField(keepScannerFocus ? 'wh-article-search' : 'qty-' + id, true);
      return;
    }
    if (current.lines.length >= 100) {
      setError(
        '100 Artikel erreicht. Diese Liste zuerst buchen, danach eine neue Liste beginnen.',
      );
      return;
    }
    setQuery('');
    if (
      !(await editDraft({
        lines: [...current.lines, { articleId: id, quantity: 1 }],
      }))
    )
      return;
    if (
      context !== scanContextRef.current ||
      current.requestId !== dataRef.current.draft?.requestId
    )
      return;
    setNotice(
      keepScannerFocus
        ? `${article(id)?.name} hinzugefügt: 1 ${article(id)?.unit}. Weiter scannen oder oben die Menge ändern.`
        : `${article(id)?.name} hinzugefügt. Jetzt die tatsächliche Gesamtmenge eintragen.`,
    );
    if (!keepScannerFocus) focusField('qty-' + id, true);
  }
  async function submitCode(raw: string, keepScannerFocus = false) {
    const code = raw.trim();
    if (!code || busy) return;
    setQuery(code);
    const matches =
      snapshot?.articles.filter((a) => warehouseCodeMatches(a, code)) ?? [];
    if (matches.length === 1) await add(matches[0].id, keepScannerFocus);
    else {
      setNotice(
        matches.length > 1
          ? 'Dieser Code gehört zu mehreren Artikeln. Bitte den richtigen Treffer auswählen.'
          : 'Kein eindeutiger Code. Vollständige Nummer scannen oder einen Suchtreffer auswählen.',
      );
      if (keepScannerFocus) focusField('wh-article-search', true);
    }
  }
  async function scan(file: File | undefined) {
    if (!file || scanning || !dataRef.current.draft) return;
    const sequence = ++scanSequence.current;
    const context = scanContextRef.current;
    const stillCurrent = () =>
      sequence === scanSequence.current && context === scanContextRef.current;
    setScanning(true);
    setError('');
    setNotice('');
    try {
      const code = await readWarehouseCode(file);
      if (!stillCurrent()) return;
      setError('');
      await submitCode(code);
    } catch (e) {
      if (stillCurrent())
        setError(
          e instanceof Error &&
            (e.message.startsWith('Code nicht erkannt') ||
              e.message.startsWith('Mehrere Codes'))
            ? e.message
            : 'Bild konnte nicht gelesen werden. Bitte ein neues Foto aufnehmen oder den Artikel suchen.',
        );
    } finally {
      if (stillCurrent()) setScanning(false);
    }
  }
  async function revise(q: WarehouseQueued) {
    if (draft) {
      setError('Zuerst die offene Artikelliste abschließen oder verwerfen.');
      setView('draft');
      return;
    }
    if (q.state !== 'rejected') return;
    const currentList = snapshot?.lists.find((x) => x.id === q.booking.listId);
    if (
      await change((s) => {
        s.draft = {
          ...structuredClone(q.booking),
          requestId: crypto.randomUUID(),
          replacesId: q.booking.requestId,
          listVersion: currentList?.version,
          checked: [],
        };
      })
    )
      setView('draft');
  }
  async function resetTest(id: string) {
    setBusy(true);
    generation.current++;
    try {
      const fresh = await resetDemo();
      await save({ snapshot: fresh, queue: [], draft: null });
      const off = ['offline', 'conflict'].includes(id);
      setTest(id);
      setOffline(off);
      onlineRef.current = !off;
      lost.current = id === 'unknown';
      await store.setItem(key + ':test', { id, offline: off });
      setView('home');
      setQuery('');
      setNotice('');
      setError('');
      setAssessment('');
      setVerification('');
      setFeedback('');
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    // Erneut von der getrennten Gegenstelle lesen, statt nur UI-Zahlen zu vergleichen.
    const actual = await transport.snapshot();
    const expected = tests.find((t) => t[0] === test)![3];
    const stock = actual.stocks.find(
      (x) => x.articleId === '12345' && x.locationId === 'central',
    )?.quantity;
    const confirmed = actual.bookings.filter((x) => x.status === 'confirmed');
    const n = test === 'interruption' ? 0 : 1;
    const extras =
      test !== 'prepared' ||
      [
        ['31001', 20],
        ['24680', 28],
        ['67890', 24.5],
      ].every(
        ([id, qty]) =>
          actual.stocks.find(
            (x) => x.articleId === id && x.locationId === 'central',
          )?.quantity === qty,
      );
    const expectedKind =
      test === 'receipt' ? 'receipt' : test === 'return' ? 'return' : 'issue';
    const expectedLines =
      test === 'prepared'
        ? [
            ['12345', 8],
            ['31001', 20],
            ['24680', 2],
          ]
        : [
            [
              '12345',
              test === 'receipt'
                ? 5
                : test === 'return'
                  ? 2
                  : test === 'conflict'
                    ? 1
                    : 3,
            ],
          ];
    const booked = confirmed[0]?.booking;
    const taskDone =
      test === 'interruption'
        ? dataRef.current.draft?.kind === 'issue' &&
          dataRef.current.draft.projectId === 'P-101' &&
          dataRef.current.draft.locationId === 'central' &&
          dataRef.current.draft.lines.length === 1 &&
          dataRef.current.draft.lines[0].articleId === '12345' &&
          dataRef.current.draft.lines[0].quantity === 3
        : booked?.kind === expectedKind &&
          booked.locationId === 'central' &&
          (test === 'receipt' || booked.projectId === 'P-101') &&
          booked.lines.length === expectedLines.length &&
          expectedLines.every(([id, qty]) =>
            booked.lines.some(
              (line) => line.articleId === id && line.quantity === qty,
            ),
          );
    const ok =
      stock === expected && confirmed.length === n && extras && taskDone;
    setVerification(
      `${ok ? 'Bestanden' : 'Noch nicht bestanden'}: Gegenstelle meldet ${stock} Kupplungen (erwartet ${expected}) und ${confirmed.length} bestätigte Vorgänge (erwartet ${n}).${test === 'prepared' ? ` Weitere Artikel: ${extras ? 'richtig' : 'abweichend'}.` : ''}${!taskDone ? ' Die geforderte Artikelliste, Buchungsart oder Zuordnung fehlt noch.' : ''}`,
    );
  }
  if (demo && !testEnabled)
    return <p>Testbereich ist hier nicht freigeschaltet.</p>;
  if (!demo && !owner)
    return (
      <section className="warehouse">
        <p>
          {isLoading
            ? 'Anmeldung wird geprüft …'
            : 'Bitte anmelden. Offline ist ein zuvor angemeldetes Konto erforderlich.'}
        </p>
        <Link to="/login" state={{ from: { pathname: '/lager' } }}>
          Zur Anmeldung
        </Link>
      </section>
    );
  if (windowAccess.key === key && windowAccess.state === 'other')
    return (
      <main className="warehouse">
        <h1>Lager ist schon geöffnet</h1>
        <p>
          Arbeite im anderen Lagerfenster weiter. Wenn du hier fortfahren
          möchtest, schließe zuerst das andere Fenster.
        </p>
        <button
          className="wh-primary wh-wide"
          onClick={() => setWindowAttempt((attempt) => attempt + 1)}
        >
          Hier weiterarbeiten
        </button>
        <Link to="/projects">Zur OpsApp</Link>
      </main>
    );
  if (windowAccess.key === key && windowAccess.state === 'unsupported')
    return (
      <main className="warehouse">
        <h1>Lager kann hier nicht sicher geöffnet werden</h1>
        <p>
          Öffne die OpsApp über ihre sichere Adresse in einem aktuellen Browser.
          Deine gespeicherten Vorgänge bleiben erhalten.
        </p>
        <Link to="/projects">Zur OpsApp</Link>
      </main>
    );
  if (!ready || loadedKey !== key)
    return (
      <main className="warehouse">
        <p role="status">Lager wird geladen …</p>
      </main>
    );
  if (!demo && data.accessDenied)
    return (
      <main className="warehouse">
        <h1>
          {data.accessDenied === 401
            ? 'Bitte erneut anmelden'
            : 'Lagerzugriff gesperrt'}
        </h1>
        <p role="alert">
          {data.accessDenied === 401
            ? 'Deine Anmeldung ist abgelaufen. Melde dich mit demselben Konto erneut an.'
            : 'Für dieses Konto fehlt die Lagerfreigabe. Bitte wende dich an die Lagerverwaltung.'}
        </p>
        <p>
          Offene Vorgänge bleiben auf diesem Gerät erhalten. Es wird nichts
          weiter übertragen.
        </p>
        {error && <p className="wh-error">{error}</p>}
        <div className="wh-actions">
          {data.accessDenied === 401 ? (
            <Link to="/login" state={{ from: { pathname: '/lager' } }}>
              Zur Anmeldung
            </Link>
          ) : (
            <button disabled={busy || !connected} onClick={() => void sync()}>
              Zugriff erneut prüfen
            </button>
          )}
          <Link to="/projects">Zur OpsApp</Link>
        </div>
        {!connected && (
          <p>Für die erneute Freigabe brauchst du eine Internetverbindung.</p>
        )}
      </main>
    );
  const list = draft?.listId
    ? snapshot?.lists.find((x) => x.id === draft.listId)
    : undefined;
  const hits =
    snapshot?.articles.filter(
      (a) =>
        !query ||
        warehouseCodeMatches(a, query) ||
        [a.number, a.name, ...a.codes].some((v) =>
          v.toLowerCase().includes(query.trim().toLowerCase()),
        ),
    ) ?? [];
  hits.sort(
    (a, b) =>
      Number(warehouseCodeMatches(b, query)) -
      Number(warehouseCodeMatches(a, query)),
  );
  const searchTerm = query.trim().toLowerCase();
  const history = [
    ...data.queue.slice().reverse(),
    ...(snapshot?.bookings ?? [])
      .filter(
        (b) => !data.queue.some((q) => q.booking.requestId === b.requestId),
      )
      .map(
        (b): WarehouseQueued => ({
          booking: b.booking,
          state: b.status,
          result: b,
        }),
      ),
  ].filter((q) => {
    const fields = [
      labels[q.booking.kind],
      status[q.state],
      q.booking.reason,
      q.booking.projectId,
      snapshot?.projects.find((p) => p.id === q.booking.projectId)?.name,
      snapshot?.locations.find((l) => l.id === q.booking.locationId)?.name,
      ...q.booking.lines.flatMap((l) => {
        const a = article(l.articleId);
        return [a?.number, a?.name, ...(a?.codes ?? [])];
      }),
    ];
    return (
      !searchTerm ||
      fields.some((value) => value?.toLowerCase().includes(searchTerm))
    );
  });
  const visibleHistory = history
    .sort(
      (a, b) =>
        Number(['pending', 'unknown', 'rejected'].includes(b.state)) -
        Number(['pending', 'unknown', 'rejected'].includes(a.state)),
    )
    .slice(0, visibleLimit);
  const projectLabel = (id: string | null) => {
    if (!id) return undefined;
    const project = snapshot?.projects.find((p) => p.id === id);
    return project ? `${project.name} · ${project.id}` : id;
  };
  return (
    <main className="warehouse">
      <header className="wh-header">
        <Link to="/projects" aria-label="Zur OpsApp">
          <img src="/360volt-logo.png" alt="360Volt" width="116" />
        </Link>
        <h1>Lager</h1>
      </header>
      <p className="wh-status" role="status">
        {busy
          ? 'Wird gespeichert …'
          : offline || !navigator.onLine
            ? 'Ohne Verbindung · Eingaben bleiben auf diesem Gerät'
            : 'Verbunden'}
        {demo && <> · Testbetrieb</>}
        {snapshot && (
          <span className="wh-data-time">
            Datenstand {new Date(snapshot.asOf).toLocaleString('de-DE')}
          </span>
        )}
      </p>
      {error && (
        <p id="wh-error" role="alert" className="wh-error" tabIndex={-1}>
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="wh-notice">
          {notice}
        </p>
      )}
      {!ready ? (
        <p>Lager wird geladen …</p>
      ) : !snapshot ? (
        <p>
          Lagerdaten fehlen. Einmal mit Internet öffnen oder die Freigabe
          prüfen.
        </p>
      ) : (
        <>
          <nav
            aria-label="Lagerbereiche"
            className="wh-nav"
            hidden={view === 'home'}
          >
            {(['home', 'stock', 'history'] as const).map((v, i) => (
              <button
                key={v}
                aria-pressed={view === v}
                onClick={() => {
                  setError('');
                  setQuery('');
                  setNotice('');
                  setView(v);
                }}
              >
                <WarehouseIcon
                  kind={
                    v === 'home' ? 'home' : v === 'stock' ? 'stock' : 'history'
                  }
                />
                {['Übersicht', 'Bestand', 'Lagerbuch'][i]}
              </button>
            ))}
          </nav>
          {draft && view !== 'draft' && view !== 'review' && (
            <button
              className="wh-resume"
              onClick={() => {
                setDraftStep(draft.lines.length ? 'items' : 'context');
                setView('draft');
              }}
            >
              Offene Liste fortsetzen · {draft.lines.length} Artikel
            </button>
          )}
          {view === 'home' && (
            <>
              <label className="wh-location">
                In welchem Lager bist du?
                <select
                  value={locationId}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  {snapshot.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
              {!demo && (
                <Link className="wh-wide wh-route-link" to="/lager/geraete">
                  Geräte und Seriennummern
                </Link>
              )}
              <h2>Was möchtest du machen?</h2>
              <WarehouseHomeActions
                disabled={busy}
                onStart={(kind) => void start(kind)}
                onStock={() => {
                  setQuery('');
                  setView('stock');
                }}
              />
              <button
                className="wh-wide"
                aria-label="Lagerbuch"
                onClick={() => {
                  setQuery('');
                  setView('history');
                }}
              >
                Buchungen ansehen
              </button>
              {data.queue.some((q) =>
                ['pending', 'unknown', 'rejected'].includes(q.state),
              ) && (
                <>
                  <button
                    className="wh-text-button"
                    disabled={busy || offline}
                    onClick={() => void sync()}
                  >
                    Übertragung prüfen
                  </button>
                  <p>
                    {
                      data.queue.filter(
                        (q) =>
                          q.state === 'pending' ||
                          q.state === 'unknown' ||
                          q.state === 'rejected',
                      ).length
                    }{' '}
                    Vorgänge noch offen
                  </p>
                </>
              )}
            </>
          )}
          {view === 'draft' && draft && (
            <>
              <WarehouseSteps step={draftStep === 'context' ? 1 : 2} />
              <div hidden={draftStep !== 'context'} className="wh-context-form">
                <h2>{labels[draft.kind]}</h2>
                <p className="wh-field-hint">
                  Diese Angaben gelten für alle Artikel auf deiner Liste.
                </p>
                <label>
                  Lagerort
                  <select
                    value={draft.locationId}
                    disabled={busy || !!draft.correctsId}
                    onChange={(e) =>
                      void editDraft({
                        locationId: e.target.value,
                      })
                    }
                  >
                    {snapshot.locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </label>
                <WarehouseProjectPicker
                  key={draft.requestId}
                  projects={snapshot.projects}
                  value={
                    draft.projectId ??
                    (draft.reason != null ? 'without-project' : '')
                  }
                  optional={draft.kind === 'receipt'}
                  disabled={busy || !!draft.correctsId}
                  onChange={(value) => {
                    setError('');
                    void editDraft({
                      projectId:
                        value === 'without-project' ? null : value || null,
                      reason: value === 'without-project' ? '' : null,
                      listId: undefined,
                      listVersion: undefined,
                    });
                  }}
                />
                {list && (
                  <p>
                    Diese Materialliste gehört zum gewählten Auftrag. Ein
                    Auftragswechsel löst die Liste; eingetragene Artikel bleiben
                    zur Prüfung erhalten.
                  </p>
                )}
                {((!draft.projectId &&
                  draft.kind !== 'receipt' &&
                  draft.reason != null) ||
                  draft.kind === 'correction') && (
                  <label>
                    Begründung
                    <input
                      maxLength={500}
                      defaultValue={draft.reason ?? ''}
                      key={draft.requestId}
                      disabled={busy}
                      onChange={(e) =>
                        void editDraft({ reason: e.target.value })
                      }
                    />
                  </label>
                )}
                {draft.kind === 'return' && (
                  <p>
                    Nur verwendbares Material zurückbuchen. Beschädigtes
                    Material separat mit der Lagerleitung klären.
                  </p>
                )}
                {draft.kind === 'correction' && (
                  <p>
                    Pro Artikel wählen, ob zu viel oder zu wenig gebucht wurde.
                    Nur die Differenz eingeben. Der Originaleintrag bleibt
                    erhalten.
                  </p>
                )}
                {draft.kind === 'issue' &&
                  !draft.listId &&
                  draft.projectId &&
                  draft.lines.length === 0 &&
                  snapshot.lists
                    .filter(
                      (l) =>
                        l.projectId === draft.projectId &&
                        l.items.some((i) => i.planned > i.issued),
                    )
                    .map((l) => {
                      const pending = data.queue.some(
                        (q) =>
                          q.booking.listId === l.id &&
                          !['cancelled', 'superseded'].includes(q.state) &&
                          !(
                            q.state === 'confirmed' &&
                            snapshot.bookings.some(
                              (b) => b.requestId === q.booking.requestId,
                            )
                          ),
                      );
                      return (
                        <div key={l.id}>
                          <button
                            disabled={busy || pending}
                            className="wh-primary wh-wide"
                            onClick={() =>
                              void editDraft({
                                listId: l.id,
                                listVersion: l.version,
                              }).then((saved) => {
                                if (saved) continueToItems();
                              })
                            }
                          >
                            Materialliste verwenden
                          </button>
                          {pending && (
                            <p>
                              Zu dieser Liste ist noch ein Vorgang offen. Zuerst
                              im Lagerbuch klären.
                            </p>
                          )}
                        </div>
                      );
                    })}
                <button
                  className="wh-primary wh-wide"
                  disabled={busy}
                  onClick={continueToItems}
                >
                  Weiter zur Artikelliste
                </button>
              </div>
              <div hidden={draftStep !== 'items'}>
                <div className="wh-context">
                  <strong>{labels[draft.kind]}</strong>
                  <span>
                    {
                      snapshot.locations.find((l) => l.id === draft.locationId)
                        ?.name
                    }
                  </span>
                  <span>
                    {projectLabel(draft.projectId) ??
                      draft.reason ??
                      'Ohne Auftrag'}
                  </span>
                </div>
                <button
                  className="wh-back"
                  onClick={() => {
                    setDraftStep('context');
                    setError('');
                  }}
                >
                  Lager oder Auftrag ändern
                </button>
                <h2 id="wh-items-title" tabIndex={-1}>
                  {draft.lines.length
                    ? `${draft.lines.length} Artikel auf der Liste`
                    : 'Welche Artikel gehören dazu?'}
                </h2>
                <p className="wh-field-hint">
                  Noch nichts gebucht.{' '}
                  {draft.lines.length
                    ? 'Prüfe die Mengen oder ergänze weitere Artikel.'
                    : 'Scanne oder suche den ersten Artikel.'}
                </p>
                {list && (
                  <section>
                    <h3>Materialliste</h3>
                    {list.items
                      .filter((i) => i.planned > i.issued)
                      .map((i) => {
                        const a = article(i.articleId)!;
                        const line = draft.lines.find(
                          (l) => l.articleId === i.articleId,
                        );
                        return (
                          <label className="wh-check" key={i.articleId}>
                            <input
                              type="checkbox"
                              checked={!!line}
                              disabled={
                                busy || (!line && draft.lines.length >= 100)
                              }
                              onChange={(e) =>
                                void editDraft({
                                  lines: e.target.checked
                                    ? [
                                        ...draft.lines,
                                        {
                                          articleId: i.articleId,
                                          quantity: i.planned - i.issued,
                                        },
                                      ]
                                    : draft.lines.filter(
                                        (l) => l.articleId !== i.articleId,
                                      ),
                                })
                              }
                            />
                            <span>
                              {a.name} · {number(i.planned - i.issued)} {a.unit}{' '}
                              noch offen
                              <br />
                              {line
                                ? `${number(line.quantity)} ${a.unit} eingepackt – Menge unten änderbar`
                                : 'Noch nicht eingepackt'}
                            </span>
                          </label>
                        );
                      })}
                  </section>
                )}
                {draft.lines.map((l) => {
                  const a = article(l.articleId)!;
                  return (
                    <article className="wh-line" key={l.articleId}>
                      <strong>{a.name}</strong>
                      <p>
                        Nr. {a.number} · Bestand{' '}
                        {number(warehouseStock(data, a.id, draft.locationId))}{' '}
                        {a.unit}
                      </p>
                      <p>
                        Regal / Fach:{' '}
                        {snapshot.stocks.find(
                          (s) =>
                            s.articleId === a.id &&
                            s.locationId === draft.locationId,
                        )?.shelf || 'Noch nicht hinterlegt'}
                      </p>
                      {draft.kind === 'correction' && (
                        <label>
                          Was war falsch?
                          <select
                            disabled={busy}
                            value={l.addInsteadOfReverse ? 'add' : 'reverse'}
                            onChange={(e) =>
                              void editDraft({
                                lines: draft.lines.map((x) =>
                                  x.articleId === a.id
                                    ? {
                                        ...x,
                                        addInsteadOfReverse:
                                          e.target.value === 'add',
                                      }
                                    : x,
                                ),
                              })
                            }
                          >
                            <option value="reverse">
                              Zu viel gebucht – Differenz zurücknehmen
                            </option>
                            <option value="add">
                              Zu wenig gebucht – Differenz ergänzen
                            </option>
                          </select>
                        </label>
                      )}
                      <WarehouseQuantity
                        key={draft.requestId + a.id}
                        id={a.id}
                        name={a.name}
                        unit={a.unit}
                        fractional={a.fractional}
                        value={l.quantity}
                        disabled={busy}
                        onChange={(quantity) =>
                          void editDraft({
                            lines: (dataRef.current.draft?.lines ?? []).map(
                              (x) =>
                                x.articleId === a.id ? { ...x, quantity } : x,
                            ),
                          })
                        }
                        onNext={
                          draft.correctsId
                            ? undefined
                            : () => focusField('wh-article-search')
                        }
                      />
                      <p
                        id={'hint-' + a.id}
                        className={
                          !Number.isFinite(l.quantity) ||
                          l.quantity <= 0 ||
                          (!a.fractional && !Number.isInteger(l.quantity))
                            ? 'wh-error'
                            : 'wh-field-hint'
                        }
                      >
                        {!Number.isFinite(l.quantity) || l.quantity <= 0
                          ? 'Bitte eine Menge größer als 0 eingeben.'
                          : !a.fractional && !Number.isInteger(l.quantity)
                            ? 'Dieser Artikel wird in ganzen Stückzahlen gebucht.'
                            : a.fractional
                              ? `+/− ändert um 1 ${a.unit}. Für z. B. 2,5 direkt auf die Zahl tippen.`
                              : 'Mit +/− zählen oder direkt auf die Zahl tippen.'}
                      </p>
                      {warehouseStock(data, a.id, draft.locationId) +
                        effect(draft, l) <
                        0 && (
                        <label>
                          Grund für Minusbestand
                          <input
                            defaultValue={l.negativeReason ?? ''}
                            disabled={busy}
                            onChange={(e) =>
                              void editDraft({
                                lines: draft.lines.map((x) =>
                                  x.articleId === a.id
                                    ? { ...x, negativeReason: e.target.value }
                                    : x,
                                ),
                              })
                            }
                          />
                        </label>
                      )}
                      <button
                        disabled={busy}
                        onClick={() =>
                          void editDraft({
                            lines: draft.lines.filter(
                              (x) => x.articleId !== a.id,
                            ),
                          })
                        }
                      >
                        Entfernen
                      </button>
                    </article>
                  );
                })}
                {!draft.correctsId && (
                  <section>
                    <h3>Artikel hinzufügen</h3>
                    <label>
                      Barcode, Artikelnummer oder Bezeichnung
                      <input
                        autoComplete="off"
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="none"
                        id="wh-article-search"
                        disabled={busy}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' ||
                            (e.key === 'Tab' &&
                              !e.shiftKey &&
                              snapshot.articles.some((a) =>
                                warehouseCodeMatches(a, query),
                              ))
                          ) {
                            e.preventDefault();
                            void submitCode(e.currentTarget.value, true);
                          }
                        }}
                      />
                    </label>
                    <p className="wh-field-hint">
                      Mit Handscanner: hier ins Feld tippen und Artikel scannen.
                      Das Feld bleibt für den nächsten Scan bereit. Mengen oben
                      separat ändern. Erneutes Scannen erhöht keine Menge.
                    </p>
                    <label className="wh-camera">
                      Mit Kamera scannen
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        disabled={busy || scanning}
                        onChange={(e) => {
                          void scan(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {hits.length > 10 && (
                      <p className="wh-field-hint">
                        {hits.length} Artikel gefunden · die ersten 10 werden
                        angezeigt. Namen oder Artikelnummer genauer eingeben
                        oder Code scannen.
                      </p>
                    )}
                    {scanning && (
                      <div>
                        <p role="status">Code wird gelesen …</p>
                        <button
                          onClick={() => {
                            ++scanSequence.current;
                            setScanning(false);
                            setNotice(
                              'Scan abgebrochen. Du kannst erneut scannen oder den Artikel suchen.',
                            );
                          }}
                        >
                          Scan abbrechen
                        </button>
                      </div>
                    )}
                    {query && (
                      <div>
                        {hits.length === 0 ? (
                          <p>
                            Kein Artikel gefunden. Nummer prüfen oder
                            Lagerleitung fragen.
                          </p>
                        ) : (
                          hits.slice(0, 10).map((a) => (
                            <button
                              className="wh-hit"
                              key={a.id}
                              disabled={busy}
                              onClick={() => void add(a.id)}
                            >
                              {a.name} · {a.number} · {a.unit}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </section>
                )}
                <div
                  className="wh-actions wh-draft-actions"
                  hidden={discardRequest === draft.requestId}
                >
                  <p>
                    {
                      snapshot.locations.find((l) => l.id === draft.locationId)
                        ?.name
                    }{' '}
                    · {projectLabel(draft.projectId) ?? 'Ohne Auftrag'} ·{' '}
                    {draft.lines.length} Artikel
                  </p>
                  {!draft.correctsId && (
                    <button
                      disabled={busy}
                      onClick={() => focusField('wh-article-search')}
                    >
                      Artikel hinzufügen
                    </button>
                  )}
                  <button
                    className="wh-primary"
                    disabled={busy || draft.lines.length === 0}
                    onClick={() => {
                      const m = validate(dataRef.current.draft!);
                      if (m) {
                        setError(m);
                        focusField('wh-error');
                      } else {
                        setError('');
                        setNotice('');
                        setView('review');
                        focusField('wh-review-title');
                      }
                    }}
                  >
                    Liste prüfen
                  </button>
                </div>
              </div>
              {discardRequest === draft.requestId ? (
                <section
                  aria-labelledby="wh-discard-title"
                  className="wh-discard"
                >
                  <h3 id="wh-discard-title">
                    Liste mit {draft.lines.length}{' '}
                    {draft.lines.length === 1 ? 'Artikel' : 'Artikeln'}{' '}
                    verwerfen?
                  </h3>
                  <p>
                    Die eingetragenen Mengen werden gelöscht. Es wurde noch
                    nichts gebucht.
                  </p>
                  <div className="wh-actions">
                    <button
                      id="wh-keep-list"
                      className="wh-primary"
                      disabled={busy}
                      onClick={() => {
                        setDiscardRequest(null);
                        focusField('wh-discard-trigger');
                      }}
                    >
                      Liste behalten
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => {
                        void change((s) => {
                          s.draft = null;
                        }).then((ok) => {
                          if (!ok) return;
                          setNotice('');
                          setQuery('');
                          setDiscardRequest(null);
                          setView('home');
                        });
                      }}
                    >
                      Ja, Liste verwerfen
                    </button>
                  </div>
                </section>
              ) : (
                <button
                  id="wh-discard-trigger"
                  className="wh-back"
                  disabled={busy}
                  onClick={() => {
                    setDiscardRequest(draft.requestId);
                    focusField('wh-keep-list');
                  }}
                >
                  Liste verwerfen
                </button>
              )}
            </>
          )}
          {view === 'review' && draft && (
            <WarehouseQuantityReview
              draft={draft}
              data={data}
              projectLabel={projectLabel}
              effect={effect}
              hasList={!!list}
              labels={labels}
              offline={offline}
              connected={connected}
              busy={busy}
              onEdit={() => {
                setView('draft');
                focusField('wh-items-title');
              }}
              onSubmit={() => void confirm()}
            />
          )}
          {view === 'stock' && (
            <section>
              <h2>Bestand nachschlagen</h2>
              <label>
                Artikel suchen
                <input
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="none"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              {hits.slice(0, visibleLimit).map((a) => (
                <article className="wh-line wh-stock-card" key={a.id}>
                  <strong>{a.name}</strong>
                  {snapshot.canManage && transport.syncHero && (
                    <WarehouseHeroStatus
                      articleId={a.id}
                      unit={a.unit}
                      status={
                        snapshot.heroSync.find((h) => h.articleId === a.id)
                          ?.status
                      }
                      disabled={busy || offline || !connected}
                      run={transport.syncHero}
                    />
                  )}
                  <p>
                    Nr. {a.number} · Gesamt{' '}
                    {number(
                      snapshot.locations.reduce(
                        (sum, l) => sum + warehouseStock(data, a.id, l.id),
                        0,
                      ),
                    )}{' '}
                    {a.unit}
                  </p>
                  <dl className="wh-stock-amounts">
                    {snapshot.locations.map((l) => (
                      <div key={l.id}>
                        <dt>
                          <WarehouseIcon kind="location" /> {l.name}
                          {snapshot.stocks.find(
                            (x) =>
                              x.articleId === a.id && x.locationId === l.id,
                          )?.shelf && (
                            <small>
                              {' '}
                              ·{' '}
                              {
                                snapshot.stocks.find(
                                  (x) =>
                                    x.articleId === a.id &&
                                    x.locationId === l.id,
                                )?.shelf
                              }
                            </small>
                          )}
                        </dt>
                        <dd>
                          {number(warehouseStock(data, a.id, l.id))} {a.unit}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <WarehouseArticleDetails
                    unit={a.unit}
                    codes={a.codes}
                    tracked={a.tracked}
                  />
                </article>
              ))}
              {hits.length > visibleLimit && (
                <button onClick={() => setVisibleLimit((n) => n + 20)}>
                  Weitere Bestände anzeigen
                </button>
              )}
              {hits.length === 0 && (
                <p>Kein passender Artikel. Nummer oder Bezeichnung prüfen.</p>
              )}
              <p>
                Offene lokale Buchungen sind vorläufig eingerechnet. Der
                angezeigte Datenstand kann offline veraltet sein.
              </p>
            </section>
          )}
          {view === 'history' && (
            <section>
              <h2>Lagerbuch</h2>
              <button disabled={busy || offline} onClick={() => void sync()}>
                Übertragung prüfen
              </button>
              <label>
                Nach Artikel, Auftrag oder Lagerort suchen
                <input
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="none"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              {visibleHistory.map((q) => (
                <article
                  className="wh-line wh-booking-card"
                  key={q.booking.requestId}
                >
                  <strong>
                    {labels[q.booking.kind]} · {status[q.state]}
                  </strong>
                  <p>
                    {
                      snapshot.locations.find(
                        (l) => l.id === q.booking.locationId,
                      )?.name
                    }{' '}
                    ·{' '}
                    {projectLabel(q.booking.projectId) ??
                      q.booking.reason ??
                      'Ohne Auftrag'}
                  </p>
                  <details
                    className="wh-details"
                    open={
                      q.state !== 'confirmed' ||
                      q.booking.requestId ===
                        data.queue[data.queue.length - 1]?.booking.requestId
                    }
                  >
                    <summary>
                      <WarehouseIcon kind="info" /> Buchungsdetails ·{' '}
                      {q.booking.lines.length} Artikelpositionen
                    </summary>
                    {q.booking.reason && <p>Begründung: {q.booking.reason}</p>}
                    {q.booking.lines.map((l) => (
                      <p key={l.articleId}>
                        {number(l.quantity)} {article(l.articleId)?.unit}{' '}
                        {article(l.articleId)?.name}
                        {' · Nr. '}
                        {article(l.articleId)?.number}
                        {q.booking.kind === 'correction'
                          ? l.addInsteadOfReverse
                            ? ' · ergänzt'
                            : ' · zurückgenommen'
                          : ''}
                      </p>
                    ))}
                  </details>
                  {q.result && (
                    <p>
                      {new Date(q.result.createdAt).toLocaleString('de-DE')} ·
                      Benutzer {q.result.actorId}
                    </p>
                  )}
                  {q.state === 'confirmed' && !demo && (
                    <p>
                      HERO-Abgleich noch nicht freigegeben. In 360 ist dieser
                      Vorgang gespeichert.
                    </p>
                  )}
                  {q.error && <p className="wh-error">{q.error}</p>}
                  {q.state === 'rejected' && (
                    <div className="wh-actions">
                      <button disabled={busy} onClick={() => void revise(q)}>
                        Menge oder Zuordnung berichtigen
                      </button>
                      <button
                        disabled={busy}
                        onClick={() =>
                          void change((s) => {
                            const entry = s.queue.find(
                              (x) =>
                                x.booking.requestId === q.booking.requestId,
                            );
                            if (entry) entry.state = 'cancelled';
                          })
                        }
                      >
                        Abgelehnten Vorgang verwerfen
                      </button>
                    </div>
                  )}
                  {q.state === 'confirmed' &&
                    snapshot.canManage &&
                    ['receipt', 'issue', 'return'].includes(q.booking.kind) && (
                      <button
                        disabled={
                          busy ||
                          !!draft ||
                          data.queue.some(
                            (x) =>
                              x.booking.correctsId === q.booking.requestId &&
                              ['pending', 'unknown', 'rejected'].includes(
                                x.state,
                              ),
                          )
                        }
                        onClick={() =>
                          void change((s) => {
                            // Die Referenz bleibt auch nach dem nächsten begrenzten Abruf verfügbar.
                            if (
                              !s.queue.some(
                                (entry) =>
                                  entry.booking.requestId ===
                                  q.booking.requestId,
                              )
                            )
                              s.queue.push({ ...q, stockIncluded: true });
                            s.draft = {
                              ...structuredClone(q.booking),
                              kind: 'correction',
                              requestId: crypto.randomUUID(),
                              correctsId: q.booking.requestId,
                              listId: undefined,
                              listVersion: undefined,
                              replacesId: undefined,
                              reason: '',
                              checked: [],
                            };
                          }).then((ok) => {
                            if (ok) setView('draft');
                          })
                        }
                      >
                        Fehlbuchung korrigieren
                      </button>
                    )}
                </article>
              ))}
              {history.length > visibleLimit && (
                <button onClick={() => setVisibleLimit((n) => n + 20)}>
                  Weitere Buchungen anzeigen
                </button>
              )}
              {snapshot.nextBookingId && transport.history && (
                <div>
                  <p className="wh-field-hint">
                    Die Suche umfasst die bisher geladenen Buchungen. Ältere
                    Einträge bei Verbindung nachladen.
                  </p>
                  <button
                    disabled={busy || loadingHistory || offline || !connected}
                    onClick={() => void loadOlderBookings()}
                  >
                    {loadingHistory
                      ? 'Ältere Buchungen werden geladen …'
                      : 'Ältere Buchungen laden'}
                  </button>
                </div>
              )}
              {history.length === 0 && (
                <p role="status">
                  {searchTerm
                    ? 'Keine passenden Vorgänge. Suchbegriff ändern.'
                    : 'Noch keine Vorgänge. Deine bestätigten Listen erscheinen hier.'}
                </p>
              )}
            </section>
          )}
        </>
      )}
      {demo && (
        <details
          className="wh-tests"
          open={testPanelOpen}
          onToggle={(e) => setTestPanelOpen(e.currentTarget.open)}
        >
          <summary>Geführter Test · Aufgabe und Ergebnis</summary>
          <label>
            Test auswählen
            <select
              value={test}
              disabled={busy}
              onChange={(e) => void resetTest(e.target.value)}
            >
              {tests.map((t) => (
                <option key={t[0]} value={t[0]}>
                  {t[1]}
                </option>
              ))}
            </select>
          </label>
          <p>
            <strong>Ausgangslage:</strong> Zentrallager: 12 Kupplungen, 24,5 m
            Rohr, 40 Schellen, 30 Dichtungen.
          </p>
          <p>
            <strong>Aufgabe:</strong> {tests.find((t) => t[0] === test)?.[2]}
          </p>
          <p>
            <strong>Erwartet:</strong> {tests.find((t) => t[0] === test)?.[3]}{' '}
            Kupplungen im Zentrallager. Jeder Test startet unabhängig.
          </p>
          <button
            className="wh-primary"
            disabled={busy}
            onClick={() =>
              void start(
                test === 'receipt'
                  ? 'receipt'
                  : test === 'return'
                    ? 'return'
                    : 'issue',
              )
            }
          >
            Aufgabe starten
          </button>
          <div className="wh-actions">
            <button disabled={busy} onClick={() => void resetTest(test)}>
              Test neu starten
            </button>
            <button
              disabled={busy}
              onClick={async () => {
                setOffline(!offline);
                await store.setItem(key + ':test', {
                  id: test,
                  offline: !offline,
                });
              }}
            >
              {offline
                ? 'Testverbindung einschalten'
                : 'Testverbindung ausschalten'}
            </button>
            {test === 'conflict' && (
              <button
                disabled={busy}
                onClick={() =>
                  void demoConflict().then(() =>
                    setAssessment(
                      'Gegenstelle: 1 Kupplung verbleibt. Jetzt verbinden.',
                    ),
                  )
                }
              >
                Andere Lagerkraft bucht 11
              </button>
            )}
            <button
              disabled={busy}
              onClick={() => void verify().catch((e) => setError(String(e)))}
            >
              Ergebnis unabhängig prüfen
            </button>
          </div>
          <p role="status">{verification || assessment}</p>
          <label>
            Dein Urteil
            <select
              value={feedback}
              onChange={(e) => {
                setFeedback(e.target.value);
                void store
                  .setItem(key + ':feedback:' + test, e.target.value)
                  .catch(() =>
                    setError('Bewertung konnte nicht gespeichert werden.'),
                  );
              }}
            >
              <option value="">Noch nicht bewertet</option>
              <option>Verständlich</option>
              <option>Unklar</option>
              <option>Fehler</option>
            </select>
          </label>
        </details>
      )}
      {demo && (
        <p className="wh-demo-badge">
          Testbetrieb · Beispieldaten · keine echten Bestandsbuchungen
        </p>
      )}
    </main>
  );
}
